import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { addRoomFile, deleteRoomFile, listRoomFiles, type ZCallFile } from '@/lib/zcall';
import { uploadMessageAttachment } from '@/lib/messageAttachment';

interface Props {
  roomId: string;
  userId: number | null;
  userName: string;
}

type Kind = 'image' | 'video' | 'pdf' | 'audio' | 'office' | 'link' | 'other';

function extOf(name: string): string {
  return name.split('?')[0].split('.').pop()?.toLowerCase() || '';
}

function detectKind(kind: string | null, name: string, url: string): Kind {
  const k = (kind || '').toLowerCase();
  const ext = extOf(name);
  if (k.startsWith('image') || ['png', 'jpg', 'jpeg', 'webp', 'gif', 'avif', 'svg'].includes(ext)) return 'image';
  if (k.startsWith('video') || ['mp4', 'webm', 'mov', 'm4v'].includes(ext)) return 'video';
  if (k.startsWith('audio') || ['mp3', 'wav', 'ogg', 'm4a'].includes(ext)) return 'audio';
  if (k === 'application/pdf' || ext === 'pdf') return 'pdf';
  if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'csv', 'txt'].includes(ext)) return 'office';
  if (/^https?:\/\//i.test(url) && ext === '') return 'link';
  return 'other';
}

function fileIcon(kind: Kind): string {
  switch (kind) {
    case 'image': return 'ri-image-line';
    case 'video': return 'ri-movie-line';
    case 'audio': return 'ri-music-2-line';
    case 'pdf': return 'ri-file-pdf-2-line';
    case 'office': return 'ri-file-text-line';
    case 'link': return 'ri-links-line';
    default: return 'ri-file-3-line';
  }
}

/**
 * Dossier partagé de la réunion.
 * Les fichiers s'ouvrent DANS la salle, dans un volet d'aperçu intégré
 * (image, vidéo, PDF, lien web). Aucune redirection vers un site externe.
 */
export default function SharedFilesPanel({ roomId, userId, userName }: Props) {
  const [files, setFiles] = useState<ZCallFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const lastIdRef = useRef<string>('');

  const refresh = useCallback(async () => {
    const list = await listRoomFiles(roomId);
    const sig = list.map((f) => f.id).join(',');
    if (sig !== lastIdRef.current) {
      lastIdRef.current = sig;
      setFiles(list);
    }
    setLoading(false);
  }, [roomId]);

  useEffect(() => { void refresh(); }, [refresh]);

  // Rafraîchissement en temps réel + repli par sondage régulier.
  useEffect(() => {
    const ch = supabase
      .channel(`zcall-files:${roomId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'zcall_files', filter: `room_id=eq.${roomId}` }, () => { void refresh(); })
      .subscribe();
    const interval = window.setInterval(() => { void refresh(); }, 8000);
    return () => { void supabase.removeChannel(ch); window.clearInterval(interval); };
  }, [roomId, refresh]);

  const selected = useMemo(() => files.find((f) => f.id === selectedId) || null, [files, selectedId]);

  const selectedKind = selected ? detectKind(selected.kind, selected.name, selected.url) : null;

  const pickFile = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    setUploading(true);
    try {
      const att = await uploadMessageAttachment(file, userId ?? 0);
      const added = await addRoomFile(roomId, {
        userId,
        userName,
        name: att.name || file.name,
        url: att.url,
        kind: att.type || file.type || 'file',
        size: file.size,
      });
      if (!added) throw new Error('insert-failed');
      await refresh();
      if (added.id) setSelectedId(added.id);
    } catch {
      setError('Impossible de partager ce fichier. Réessayez.');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const remove = async (f: ZCallFile) => {
    setFiles((prev) => prev.filter((x) => x.id !== f.id));
    if (selectedId === f.id) setSelectedId(null);
    await deleteRoomFile(f.id);
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 flex-shrink-0">
        <div className="flex items-center gap-2">
          <i className="ri-folder-shared-line text-accent-400"></i>
          <span className="text-sm font-semibold text-white">Dossier partagé</span>
        </div>
        <span className="text-[10px] text-white/50">{files.length} fichier{files.length !== 1 ? 's' : ''}</span>
      </div>

      <div className="p-3 flex-shrink-0">
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="w-full py-2.5 rounded-lg bg-accent-500 text-white text-sm font-semibold cursor-pointer hover:bg-accent-600 disabled:opacity-50 transition-colors flex items-center justify-center gap-2 whitespace-nowrap"
        >
          <i className={uploading ? 'ri-loader-4-line animate-spin' : 'ri-upload-2-line'}></i>
          {uploading ? 'Envoi…' : 'Partager un fichier'}
        </button>
        <input ref={fileRef} type="file" className="hidden" onChange={(e) => pickFile(e.target.files?.[0])} />
        {error && <p className="text-[11px] text-red-400 mt-2">{error}</p>}
      </div>

      {/* Aperçu intégré : le fichier s'ouvre ici, dans la réunion */}
      {selected && selectedKind && (
        <div className="mx-3 mb-3 rounded-lg overflow-hidden border border-white/15 bg-white/5 flex-shrink-0">
          <div className="flex items-center gap-2 px-2.5 py-2 border-b border-white/10">
            <i className={`${fileIcon(selectedKind)} text-accent-300 text-sm flex-shrink-0`}></i>
            <span className="text-[11px] text-white font-medium truncate flex-1">{selected.name}</span>
            <button
              type="button"
              onClick={() => setSelectedId(null)}
              className="w-6 h-6 flex items-center justify-center rounded-full text-white/50 hover:text-white hover:bg-white/10 cursor-pointer flex-shrink-0"
              title="Fermer l'aperçu"
            >
              <i className="ri-close-line text-xs"></i>
            </button>
          </div>

          <div className="bg-black/40">
            {selectedKind === 'image' && (
              <img src={selected.url} alt={selected.name} className="w-full max-h-56 object-contain" />
            )}
            {selectedKind === 'video' && (
              <video src={selected.url} controls className="w-full max-h-56 bg-black" />
            )}
            {selectedKind === 'audio' && (
              <div className="p-3">
                <audio src={selected.url} controls className="w-full" />
              </div>
            )}
            {(selectedKind === 'pdf' || selectedKind === 'link' || selectedKind === 'office') && (
              <>
                <iframe
                  src={selectedKind === 'office'
                    ? `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(selected.url)}`
                    : selected.url}
                  title={selected.name}
                  className="w-full h-56 bg-white"
                  loading="lazy"
                ></iframe>
                <p className="text-[10px] text-white/40 px-2.5 py-1.5">
                  {selectedKind === 'office'
                    ? 'Aperçu du document — utilisez le bouton ci-dessous si le rendu est indisponible.'
                    : "Si l'aperçu reste vide, ce site interdit l'intégration."}
                </p>
              </>
            )}
            {selectedKind === 'other' && (
              <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
                <i className="ri-file-3-line text-2xl text-white/30 mb-2"></i>
                <p className="text-[11px] text-white/50">Aperçu non disponible pour ce format.</p>
              </div>
            )}
          </div>

          {selectedKind !== 'image' && selectedKind !== 'video' && selectedKind !== 'audio' && (
            <a
              href={selected.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-1.5 py-2 text-[11px] font-medium text-accent-300 hover:text-accent-200 hover:bg-white/5 transition-colors no-underline"
            >
              <i className="ri-download-2-line"></i>
              Ouvrir dans un nouvel onglet
            </a>
          )}
        </div>
      )}

      <div className="flex-1 overflow-y-auto px-3 pb-3 space-y-1.5">
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <i className="ri-loader-4-line animate-spin text-xl text-white/40"></i>
          </div>
        ) : files.length === 0 ? (
          <div className="text-center py-10 px-4">
            <i className="ri-folder-open-line text-3xl text-white/20"></i>
            <p className="text-xs text-white/40 mt-2">Aucun fichier partagé pour l&apos;instant.</p>
            <p className="text-[10px] text-white/30 mt-1">Déposez ici les documents à présenter pendant la réunion.</p>
          </div>
        ) : (
          files.map((f) => {
            const kind = detectKind(f.kind, f.name, f.url);
            const active = selectedId === f.id;
            return (
              <div
                key={f.id}
                className={`flex items-center gap-2.5 p-2 rounded-lg transition-colors group ${active ? 'bg-accent-500/20 ring-1 ring-accent-500/40' : 'bg-white/5 hover:bg-white/10'}`}
              >
                <button
                  type="button"
                  onClick={() => setSelectedId(active ? null : f.id)}
                  className="flex items-center gap-2.5 flex-1 min-w-0 text-left cursor-pointer"
                  title="Afficher dans la réunion"
                >
                  <span className="w-9 h-9 rounded-md bg-white/10 flex items-center justify-center flex-shrink-0">
                    <i className={`${fileIcon(kind)} text-white/80`}></i>
                  </span>
                  <span className="flex-1 min-w-0 block">
                    <span className="block text-xs font-medium text-white truncate">{f.name}</span>
                    <span className="block text-[10px] text-white/40 truncate">{f.user_name || 'Participant'}</span>
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedId(active ? null : f.id)}
                  className="w-7 h-7 flex items-center justify-center rounded-full text-white/60 hover:text-white hover:bg-white/10 cursor-pointer flex-shrink-0"
                  title="Afficher dans la réunion"
                >
                  <i className="ri-eye-line text-xs"></i>
                </button>
                {userId != null && f.user_id === userId && (
                  <button
                    type="button"
                    onClick={() => remove(f)}
                    className="w-7 h-7 flex items-center justify-center rounded-full text-white/40 hover:text-red-400 hover:bg-white/10 cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                    title="Retirer"
                  >
                    <i className="ri-delete-bin-line text-sm"></i>
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}